import { FormProvider, useFormContext } from 'contexts/formContext';
import SelectConsignmentForm from './SelectConsignmentForm';
// import { parse } from 'date-fns';
import {
    defaultValuesSiape,
    defaultValuesZetra,
    TSelectConsignmentPropsSiape,
    TSelectConsignmentPropsZetra,
    validationSchemaSiape,
    validationSchemaZetra,
} from './SelectConsignmentSchema';
import { FieldErrors } from 'react-hook-form';
import { CheckPurchaseContractsReadModel } from 'services/zetra';
import { showInfoToast, ToastType } from 'contexts/apiRequestContext';
import { IWarrantyReadModel, mapConsignmentWarranty } from 'services/creditNote';
import { CreditProductReadModel } from 'services/creditProduct';
import { setPaymentTransferAndCutoffDay } from 'pages/CreditNote/CreditNoteForm/Tabs/Operation/setFirstPaymentDate';
import { activeConfig } from 'services/config';
import { transformPurchaseContracts } from '../SummaryPortability';

type SelectConsignmentProps = {
    setOpenSelectConsignment: Function;
    setUpdateConsignment: Function;
    checkPurchaseContracts: CheckPurchaseContractsReadModel | undefined;
    setToast: React.Dispatch<React.SetStateAction<ToastType>>;
    onClose: () => void;
    registrationNumber: string;
    productData: CreditProductReadModel | undefined;
    personId?: string;
};
interface ZetraContract {
    adeNumero: string;
    adeIdentificador: string;
    prazo: number;
    valorParcela?: number;
    paymentAmountInCents?: number;
}

interface SiapeContract {
    employeeCode: string;
    contratoPortado?: {
        NrContrato: string;
    };
    dtValidadePortabilidade?: string;
    paymentAmountInCents?: number;
    warrantyType?: string;
    governmentDepartmentCode?: string;
    agreementCode?: string;
    valorParcela?: number;
    prazo?: number;
}
const config = activeConfig();

function SelectConsignmentContainer({
    setOpenSelectConsignment,
    setUpdateConsignment,
    checkPurchaseContracts,
    setToast,
    onClose,
    registrationNumber,
    productData,
    personId,
}: SelectConsignmentProps) {
    const { setValue, watch } = useFormContext();
    const watchedWarranty = (watch('warranty') ?? []) as IWarrantyReadModel[];
    const isSiape = productData?.warrantyRegistrationOffice?.toLowerCase() === 'siape';
    const isZetra = productData?.warrantyRegistrationOffice?.toLowerCase() === 'zetra';

    const handleSubmit = (props: TSelectConsignmentPropsZetra | TSelectConsignmentPropsSiape) => {
        const {
            selectConsignment,
            warrantyRegistrationCutoffDay,
            paymentTransferDay,
            warrantyDeadline,
            ...rest
        } = (props ?? {}) as Record<string, any>;

        if (isZetra) {
            processZetraFlow(
                selectConsignment,
                warrantyRegistrationCutoffDay,
                paymentTransferDay,
                rest
            );
        } else if (isSiape) {
            processSiapeFlow(selectConsignment, warrantyDeadline);
        }
    };

    const processZetraFlow = (
        selectConsignment: string[],
        warrantyRegistrationCutoffDay: number | undefined,
        paymentTransferDay: number | undefined,
        rest: Record<string, any>
    ) => {
        const abstracts = checkPurchaseContracts?.resumos?.map((resumo) => ({
            ...resumo,
            adeNumero: resumo.adeNumero.toString(),
        })) as ZetraContract[];

        const selectedItems = filterSelectedItemsZetra(abstracts ?? [], selectConsignment);

        if (!!selectedItems?.length) {
            const maxTermInMonths = calculateMaxTermInMonths(selectedItems);
            const mappedWarrantyModels = mapWarrantyModelsZetra(
                selectedItems,
                rest,
                maxTermInMonths,
                paymentTransferDay
            );

            updateAmortizationValues(
                selectedItems,
                mappedWarrantyModels,
                warrantyRegistrationCutoffDay,
                paymentTransferDay,
                maxTermInMonths
            );
        }

        finalizeFlow();
    };

    const processSiapeFlow = (
        selectConsignment: string[],
        warrantyDeadline: number | undefined
    ) => {
        const result = transformPurchaseContracts(checkPurchaseContracts) as SiapeContract[];
        const defaultValueAmortization = watch('amortization.numberOfPayments');

        const selectedItems = filterSelectedItemsSiape(result, selectConsignment);

        if (!!selectedItems?.length) {
            const mappedWarrantyModels = mapWarrantyModelsSiape(selectedItems, warrantyDeadline);

            updateAmortizationValues(
                selectedItems,
                mappedWarrantyModels,
                undefined,
                undefined,
                defaultValueAmortization
            );
        }

        finalizeFlow();
    };

    const filterSelectedItemsZetra = (abstracts: ZetraContract[], selectConsignment: string[]) => {
        return abstracts.filter((f) => {
            const value = `${f.adeNumero}-${f.adeIdentificador}-${f.prazo}`;
            return selectConsignment.includes(value);
        });
    };

    const filterSelectedItemsSiape = (result: SiapeContract[], selectConsignment: string[]) => {
        return result.filter((item) =>
            selectConsignment.includes(`${item.employeeCode}-${item.contratoPortado?.NrContrato}`)
        );
    };

    const calculateMaxTermInMonths = (selectedItems: ZetraContract[]) => {
        return selectedItems.reduce((acc, curr) => Math.max(acc, curr.prazo || 0), 0);
    };

    const mapWarrantyModelsZetra = (
        selectedItems: ZetraContract[],
        rest: Record<string, any>,
        maxTermInMonths: number,
        paymentTransferDay: number | undefined
    ) => {
        return [
            ...watchedWarranty.map((item) => {
                if (
                    watchedWarranty.filter((i) => i.warrantyOption === 'Portability').length >=
                        config.QUANTITY_CONSIGNMENT_SELECT &&
                    item.warrantyOption === 'Portability'
                ) {
                    return null;
                }
                return item;
            }),
            ...selectedItems.map((item) => {
                const isTheSameWarranty = watchedWarranty.find(
                    (i) =>
                        i.consignmentValue ===
                        `${item.adeNumero}-${item.adeIdentificador}-${item.prazo}`
                );

                if (isTheSameWarranty) {
                    return null;
                }
                const additionalInformation = {
                    ...rest,
                    adeNumero: item.adeNumero,
                    paymentTransferDay: paymentTransferDay,
                    maxTermInMonths,
                };

                return mapConsignmentWarranty({
                    resumo: item as any,
                    newValues: additionalInformation,
                    registrationNumber,
                });
            }),
        ]
            .filter((item) => !!item)
            .map((item) => item as IWarrantyReadModel);
    };

    const mapWarrantyModelsSiape = (
        selectedItems: SiapeContract[],
        warrantyDeadline: number | undefined
    ) => {
        return [
            ...watchedWarranty.map((item) => {
                if (
                    watchedWarranty.filter((i) => i.warrantyOption === 'Portability').length >=
                        config.QUANTITY_CONSIGNMENT_SELECT &&
                    item.warrantyOption === 'Portability'
                ) {
                    return null;
                }
                return item;
            }),
            ...selectedItems.map((item) => {
                const isTheSameWarranty = watchedWarranty.find(
                    (i) =>
                        i.consignmentValue ===
                        `${item.employeeCode}-${item.contratoPortado?.NrContrato}`
                );

                if (isTheSameWarranty) {
                    return null;
                }

                return {
                    warrantyType: item.warrantyType ?? 'PublicPayroll',
                    warrantyOption: 'Portability',
                    governmentDepartmentCode: item.governmentDepartmentCode ?? null,
                    paymentTransferDay: null,
                    employeeCode: item.employeeCode,
                    paymentAmountInCents: item.paymentAmountInCents ?? 0,
                    warrantyDeadline: warrantyDeadline,
                    previousAdeNumber: item.contratoPortado?.NrContrato ?? null,
                    simulationRegistrationNumber: registrationNumber,
                    termInMonths: item.dtValidadePortabilidade ? 10 : 0,
                    consignmentValue: `${item.employeeCode}-${item.contratoPortado?.NrContrato}`,
                    agreementCode: item.agreementCode ?? null,
                };
            }),
        ]
            .filter((item) => !!item)
            .map((item) => item as IWarrantyReadModel);
    };

    const updateAmortizationValues = (
        selectedItems: ZetraContract[] | SiapeContract[],
        mappedWarrantyModels?: IWarrantyReadModel[],
        warrantyRegistrationCutoffDay?: number | undefined,
        paymentTransferDay?: number | undefined,
        maxTermInMonths?: number
    ) => {
        let requestedAmount = 0;

        if (isZetra) {
            requestedAmount =
                (selectedItems as ZetraContract[]).reduce(
                    (acc, curr) => acc + (curr.valorParcela ?? 0),
                    0
                ) * 100;
        } else {
            requestedAmount = (selectedItems as SiapeContract[]).reduce(
                (acc, curr) => acc + (curr.paymentAmountInCents ?? 0),
                0
            );
        }

        if (warrantyRegistrationCutoffDay && paymentTransferDay) {
            const startDateString = watch('amortization.startDate');
            if (startDateString) {
                setPaymentTransferAndCutoffDay(
                    startDateString,
                    paymentTransferDay,
                    warrantyRegistrationCutoffDay,
                    setValue
                );
            }
        }

        setValue('amortization.requestedAmount', requestedAmount);
        setValue('warranty', mappedWarrantyModels);
        setValue('amortization.calculateByValueType', 'Payment');
        setValue('amortization.termInMonths', maxTermInMonths);
        setValue('amortization.numberOfPayments', maxTermInMonths);
    };

    const finalizeFlow = () => {
        onClose();
        setOpenSelectConsignment(false);
    };

    return (
        <FormProvider
            validationSchema={isZetra ? validationSchemaZetra : validationSchemaSiape}
            defaultValues={isZetra ? defaultValuesZetra : defaultValuesSiape}
            onSubmit={handleSubmit}
            onError={(
                err: FieldErrors<TSelectConsignmentPropsSiape | TSelectConsignmentPropsZetra>
            ) => {
                const erroMessage = Object.values(err)
                    .map((error) => error?.message)
                    .join('\n');
                showInfoToast('Atenção! Verifique os campos obrigatórios.', erroMessage, setToast);
            }}
        >
            <SelectConsignmentForm
                {...{
                    setOpenSelectConsignment,
                    setUpdateConsignment,
                    checkPurchaseContracts,
                    productData,
                    personId,
                    warrantyList: watchedWarranty,
                }}
            />
        </FormProvider>
    );
}

export default SelectConsignmentContainer;
